import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Inject,
  Output,
} from '@angular/core';
import {LOCAL_STORAGE, WINDOW} from '@ng-web-apis/common';
import {DOCUMENT} from '@angular/common';
import {Store} from '@ngxs/store';
import {Login} from '@store/auth/auth.actions';
import {
  combineLatest,
  filter,
  fromEvent,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import {UserState} from '@store/user/user.state';
import {environment} from '@environments/environment';
import {TuiDestroyService} from '@taiga-ui/cdk';
import {WicketUser} from '@tisa-group/models/wicket/wicket-user';
import {AuthService} from '@core/services/auth.service';
import {RouteNames} from '@core/constants/routes';
import {BlockedCountiresState} from '@store/blocked-countries/blocked-countries.state';
import {Language, LanguageService} from '@core/services/language.service';
import {ASSET_MAP, AssetMap} from '@core/constants/client-asset-maps';
import {LoadGlobalPageElementBySlug} from '@store/global-page-element/global-page-element.actions';
import {
  UmpireComponentBranding,
  UmpireComponentHeader,
  UmpireComponentTypeEnum,
  UmpireGlobalPageElementSlugEnum,
  UmpireLinkTypeEnum,
} from '@tisa-group/models';
import {GlobalPageElementState} from '@store/global-page-element/global-page-element-state.service';

import {TISA_WICKET_V2_API_URL} from '@tisa-group/core/api/wicket/tisa-wicket.providers';
import {CurrentClientState} from '@store/current-client/current-client.state';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TuiDestroyService],
  standalone: false,
})
export class HeaderComponent {
  readonly templatePagesSlugsBlockedByCountry =
    environment.clientConfig.templatePagesSlugsBlockedByCountry;

  readonly RouteNames = RouteNames;

  readonly wicketV2Url = inject(TISA_WICKET_V2_API_URL);

  @Output()
  readonly languageChanged = new EventEmitter<Language>();

  readonly isLoadingUser$: Observable<boolean> = this.store.select(
    UserState.isLoadingUserData,
  );

  readonly user$ = this.store.select(UserState.user);

  isDarkTheme: boolean | null | undefined;

  expanded = false;

  headerStaticLinks$ = of([
    {
      label: 'views.offer',
      routerLink: `${RouteNames.offer}`,
      hidden$: this.store.select(BlockedCountiresState.isInBlockedCountry),
      hidden:
        !environment.clientConfig.views?.showOffer &&
        !this.store.selectSnapshot(CurrentClientState.getData)?.config
          .external_dependencies.wicket?.chargebee_site,
    },
    {
      label: 'views.table',
      routerLink: `${RouteNames.table}`,
      hidden: !environment.clientConfig.views?.showStandings,
    },
    {
      label: 'views.clubs',
      routerLink: `${RouteNames.clubs}`,
      hidden: !environment.clientConfig.views?.showClubs,
    },
    {
      label: 'views.faq',
      routerLink: `${RouteNames.faq}`,
      hidden: environment.name !== 'ekstraklasa',
    },
  ]);

  headerLogo$ = this.store
    .select(
      GlobalPageElementState.getGlobalPageElementBySlug(
        UmpireGlobalPageElementSlugEnum.BRANDING,
      ),
    )
    .pipe(
      map(
        globalPageElement =>
          globalPageElement?.components?.find(
            component => component.kind === UmpireComponentTypeEnum.BRANDING,
          ) as UmpireComponentBranding,
      ),
      map(branding => branding?.logo),
    );

  header$ = this.store
    .dispatch(
      new LoadGlobalPageElementBySlug(UmpireGlobalPageElementSlugEnum.HEADER_FOOTER, {
        include: 'components,components.links',
      }),
    )
    .pipe(
      switchMap(() =>
        this.store.select(
          GlobalPageElementState.getGlobalPageElementBySlug(
            UmpireGlobalPageElementSlugEnum.HEADER_FOOTER,
          ),
        ),
      ),
      map(data => {
        return data?.components?.find(
          item => item.kind === UmpireComponentTypeEnum.PAGE_HEADER,
        ) as UmpireComponentHeader;
      }),
      shareReplay(1),
    );

  headerDynamicLinks$ = combineLatest([
    this.header$.pipe(
      filter(header => !!header),
      map(header => header?.links.map(link => link.resource.data)),
      map(links =>
        links.filter(
          link =>
            !(
              link.type === UmpireLinkTypeEnum.PAGE_TYPE &&
              link.attributes.slug === 'home'
            ),
        ),
      ),
    ),
    this.store
      .select(BlockedCountiresState.isInBlockedCountry)
      .pipe(filter(state => state !== null)),
  ]).pipe(
    map(([links, isInBlockedCountry]) =>
      links.map(link => {
        switch (link.type) {
          case UmpireLinkTypeEnum.PAGE_TYPE:
            return {
              label: link.attributes.name,
              routerLink: `${link.attributes.slug}`,
              hidden:
                this.templatePagesSlugsBlockedByCountry?.includes(link.attributes.slug) &&
                isInBlockedCountry,
            };
          case UmpireLinkTypeEnum.ARTICLE:
            return {
              label: link.attributes.title,
              routerLink: `page/${link.attributes.slug}`,
              // TODO: change to false when static pages are handled
              hidden: true,
            };
          case 'component':
            return {
              label: link.attributes.name,
              href: link.attributes.url,
              hidden: false,
            };
          default: {
            throw new Error(`Unsupported link type: ${link.type}`);
          }
        }
      }),
    ),
  );

  readonly links$ = combineLatest([
    this.headerDynamicLinks$,
    this.headerStaticLinks$,
  ]).pipe(
    map(([headerDynamicLinks, staticLinks]) => [...headerDynamicLinks, ...staticLinks]),
  );

  isScrolledToTop$ = fromEvent(this.document.body, 'scroll').pipe(
    startWith(null),
    map(() => this.document.body.scrollTop < 25),
  );

  constructor(
    @Inject(DOCUMENT) readonly document: Document,
    @Inject(LOCAL_STORAGE) readonly localStorage: Storage,
    @Inject(WINDOW) readonly window: Window,
    @Inject(ASSET_MAP) readonly assetMap: AssetMap,
    readonly store: Store,
    readonly destroy$: TuiDestroyService,
    readonly changeDetectorRef: ChangeDetectorRef,
    readonly authService: AuthService,
    readonly languageService: LanguageService,
  ) {}

  toggleMobileNavigation(): void {
    if (this.expanded) {
      this.closeMobileNavigation();
    } else {
      this.openMobileNavigation();
    }
  }

  closeMobileNavigation(): void {
    this.expanded = false;
    this.document.querySelector('body')?.classList.remove('mobile-nav-open');
  }

  openMobileNavigation(): void {
    this.expanded = true;
    this.document.querySelector('body')?.classList.add('mobile-nav-open');
  }

  getUserAvatar(user: WicketUser): string | undefined {
    if (!user.profile_photo) {
      return;
    }

    if (user.profile_photo.includes('http')) {
      return user.profile_photo;
    }
    const wicketImages = this.store.selectSnapshot(CurrentClientState.getData)?.config
      .external_dependencies.wicket?.images_url;

    return `${wicketImages?.includes('http') ? '' : 'https://'}/${wicketImages}/${
      user.profile_photo
    }`;
  }

  login() {
    this.store.dispatch(Login);
  }

  goToUserProfile(): void {
    this.authService.goToUserProfile();
  }

  changeLanguage(newLanguage: Language) {
    this.languageChanged.emit(newLanguage);
  }

  get wicketLoginEnabled() {
    return this.wicketV2Url instanceof Function
      ? !!this.wicketV2Url()
      : !!this.wicketV2Url;
  }
}
